import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ArrowDownUp, RefreshCw, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import stonfiService from '../services/stonfiService';

const STONSwap = ({ walletAddress }) => {
  const [pools, setPools] = useState([]);
  const [tokenIn, setTokenIn] = useState('native');
  const [tokenOut, setTokenOut] = useState('');
  const [amountIn, setAmountIn] = useState('');
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [swapping, setSwapping] = useState(false);

  useEffect(() => {
    fetchPools();
  }, []);

  const fetchPools = async () => {
    try {
      const data = await stonfiService.getPools();
      setPools(data);
      if (data.length > 0 && !tokenOut) {
        setTokenOut(data[0].token1_address);
      }
    } catch (error) {
      toast.error('Failed to load pools');
    }
  };

  const handleEstimate = async () => {
    if (!amountIn || parseFloat(amountIn) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (!tokenOut) {
      toast.error('Please select output token');
      return;
    }

    setLoading(true);
    try {
      // Convert TON to nanotons
      const amountInNano = (parseFloat(amountIn) * 1_000_000_000).toString();
      
      const data = await stonfiService.estimateSwap(tokenIn, tokenOut, amountInNano);
      setEstimate(data);
      
      toast.success('Swap estimated successfully');
    } catch (error) {
      toast.error('Failed to estimate swap', {
        description: error.response?.data?.detail || error.message,
      });
      setEstimate(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSwap = async () => {
    if (!walletAddress) {
      toast.error('Please connect your wallet first');
      return;
    }

    if (!estimate) {
      toast.error('Please estimate swap first');
      return;
    }

    setSwapping(true);
    try {
      const result = await stonfiService.executeSwap({
        walletAddress,
        tokenIn,
        tokenOut,
        amountIn: estimate.amount_in,
        minAmountOut: estimate.minimum_received,
        slippageTolerance: 0.01,
      });

      toast.success('Swap executed!', {
        description: `Transaction: ${result.tx_hash}`,
      });

      // Reset form
      setAmountIn('');
      setEstimate(null);
    } catch (error) {
      toast.error('Failed to execute swap', {
        description: error.response?.data?.detail || error.message,
      });
    } finally {
      setSwapping(false);
    }
  };

  const handleSwapTokens = () => {
    const temp = tokenIn;
    setTokenIn(tokenOut);
    setTokenOut(temp);
    setEstimate(null);
  };

  const getTokenName = (address) => {
    if (address === 'native') return 'TON';
    const pool = pools.find(
      (p) => p.token0_address === address || p.token1_address === address
    );
    if (!pool) return 'Unknown';
    return pool.token0_address === address ? pool.token0 : pool.token1;
  };

  const formatAmount = (nanotons) => {
    if (!nanotons) return '0';
    return (parseFloat(nanotons) / 1_000_000_000).toFixed(4);
  };

  return (
    <Card data-testid="ston-swap-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          Swap on STON.fi
        </CardTitle>
        <CardDescription>
          Trade tokens on TON's leading DEX
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* From Token */}
        <div className="space-y-2">
          <Label>From</Label>
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                type="number"
                placeholder="0.0"
                value={amountIn}
                onChange={(e) => {
                  setAmountIn(e.target.value);
                  setEstimate(null);
                }}
                className="text-2xl font-bold h-14"
                data-testid="amount-in-input"
              />
            </div>
            <div className="w-32">
              <Select value={tokenIn} onValueChange={setTokenIn}>
                <SelectTrigger className="h-14">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="native">TON</SelectItem>
                  {pools.map((pool) => (
                    <SelectItem key={pool.token1_address} value={pool.token1_address}>
                      {pool.token1}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Swap Direction Button */}
        <div className="flex justify-center">
          <Button
            variant="outline"
            size="icon"
            onClick={handleSwapTokens}
            className="rounded-full"
            data-testid="swap-direction-btn"
          >
            <ArrowDownUp className="h-4 w-4" />
          </Button>
        </div>

        {/* To Token */}
        <div className="space-y-2">
          <Label>To (estimated)</Label>
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                type="text"
                placeholder="0.0"
                value={estimate ? formatAmount(estimate.amount_out) : ''}
                readOnly
                className="text-2xl font-bold h-14 bg-gray-50"
                data-testid="amount-out-display"
              />
            </div>
            <div className="w-32">
              <Select value={tokenOut} onValueChange={setTokenOut}>
                <SelectTrigger className="h-14">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {pools
                    .filter((pool) => pool.token1_address !== tokenIn)
                    .map((pool) => (
                      <SelectItem key={pool.token1_address} value={pool.token1_address}>
                        {pool.token1}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Estimate Details */}
        {estimate && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Price Impact</span>
              <span className="font-medium">{estimate.price_impact}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Trading Fee</span>
              <span className="font-medium">{formatAmount(estimate.fee)} TON</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Minimum Received</span>
              <span className="font-medium">
                {formatAmount(estimate.minimum_received)} {getTokenName(tokenOut)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Route</span>
              <span className="font-medium font-mono text-xs">
                {estimate.route.map(getTokenName).join(' → ')}
              </span>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-2">
          <Button
            onClick={handleEstimate}
            disabled={loading || !amountIn}
            className="w-full"
            variant="outline"
            data-testid="estimate-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Estimating...' : 'Get Quote'}
          </Button>

          <Button
            onClick={handleSwap}
            disabled={!estimate || swapping || !walletAddress}
            className="w-full"
            data-testid="swap-btn"
          >
            {swapping ? 'Swapping...' : !walletAddress ? 'Connect Wallet' : 'Swap'}
          </Button>
        </div>

        {/* Info */}
        <p className="text-xs text-center text-gray-500">
          Slippage tolerance: 1% • Network: Testnet
        </p>
      </CardContent>
    </Card>
  );
};

export default STONSwap;
